import { useCallback } from "react";
import { toast } from "sonner";
import { BASIC_SHAPES, TEMPLATES, ANIMATION_PRESETS } from "../templates";
import { bboxOf, unionBbox, translateElement } from "../canvasUtils";
import { nanoIdLike } from "../utils";

// Read a File (image) into a data URL. Used for drag/drop and paste uploads.
export function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type || !file.type.startsWith("image/")) {
      reject(new Error("Not an image file"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

// Load an image data URL and resolve its natural dimensions, so we can place
// the element at a sensible size that preserves aspect ratio.
export function loadImageSize(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = dataUrl;
  });
}

// Detect and crop transparent padding around an image so the visible content
// fills the element box. Without this, PNGs exported from design tools (which
// often sit on a large transparent canvas) render far smaller than their
// <image> tag, making corner-radius clipping look wrong — the rounded clip is
// applied to the full tag, but the visible pixels only occupy the centre.
//
// Returns a new data URL (re-encoded as PNG) with the padding removed, plus
// the trimmed dimensions. If the image has no meaningful transparency (e.g.
// a JPEG, or a PNG that is fully opaque), it is returned unchanged.
export async function trimTransparentPadding(dataUrl) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("Failed to load image for trim"));
    i.src = dataUrl;
  });

  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  if (!nw || !nh) return { dataUrl, width: 0, height: 0 };

  const canvas = document.createElement("canvas");
  canvas.width = nw;
  canvas.height = nh;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);
  const { data } = ctx.getImageData(0, 0, nw, nh);

  // Scan for the bounding box of non-transparent pixels (alpha > threshold).
  // A higher threshold (30) also crops semi-transparent anti-aliased edges
  // that design tools leave around exported PNGs — these look like "padding"
  // to the eye even though their alpha is non-zero, which is why a low
  // threshold (8) leaves the image looking smaller than its tag on the
  // left/right/bottom sides.
  const THRESH = 30;
  let minX = nw, minY = nh, maxX = -1, maxY = -1;
  for (let y = 0; y < nh; y++) {
    for (let x = 0; x < nw; x++) {
      if (data[(y * nw + x) * 4 + 3] > THRESH) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  // No content found, or content fills the entire canvas — nothing to trim.
  if (maxX < 0 || (minX === 0 && minY === 0 && maxX === nw - 1 && maxY === nh - 1)) {
    return { dataUrl, width: nw, height: nh };
  }

  const tw = maxX - minX + 1;
  const th = maxY - minY + 1;
  const trimmed = document.createElement("canvas");
  trimmed.width = tw;
  trimmed.height = th;
  trimmed.getContext("2d").drawImage(canvas, minX, minY, tw, th, 0, 0, tw, th);
  return { dataUrl: trimmed.toDataURL("image/png"), width: tw, height: th };
}

// Cap the placed image so very large uploads don't dwarf the canvas. Keeps
// aspect ratio; the longer edge is clamped to `maxEdge`.
function fitDimensions(natW, natH, maxEdge = 320) {
  if (!natW || !natH) return { width: maxEdge, height: maxEdge };
  const longest = Math.max(natW, natH);
  if (longest <= maxEdge) return { width: natW, height: natH };
  const k = maxEdge / longest;
  return { width: Math.round(natW * k), height: Math.round(natH * k) };
}

// Element manipulation actions (create / update / delete / animate / batch).
export function useElementActions({ commit, selectedIds, setSelectedIds }) {
  const updateElement = useCallback(
    (id, patch) => {
      commit((prev) =>
        prev.map((el) => {
          if (el.id !== id) return el;
          // `patch` may be a function (el) => patch so callers can read the
          // freshest element state (avoids stale-closure bugs when several
          // updates are issued in the same tick from a snapshot prop).
          const resolved = typeof patch === "function" ? patch(el) : patch;
          // For images, keep origX/origY/origW/origH in sync when the display
          // box (x/y/width/height) is changed directly via the Transform panel
          // or other attribute edits. Move edits shift orig by the same delta;
          // size edits scale orig proportionally so crop fractions stay valid.
          if (el.type === "image" && resolved.attrs) {
            const oldA = el.attrs;
            const newA = resolved.attrs;
            const merged = { ...oldA, ...newA };
            if (oldA.origX != null) {
              // x/y change -> translate orig by the same delta
              if (newA.x != null && newA.x !== oldA.x) {
                merged.origX = (oldA.origX ?? 0) + (newA.x - (oldA.x ?? 0));
              }
              if (newA.y != null && newA.y !== oldA.y) {
                merged.origY = (oldA.origY ?? 0) + (newA.y - (oldA.y ?? 0));
              }
              // width/height change -> scale orig proportionally (anchor at x/y)
              if (newA.width != null && oldA.width && newA.width !== oldA.width) {
                const k = newA.width / oldA.width;
                merged.origW = (oldA.origW ?? 0) * k;
                // origX shifts so the crop region (anchored at x) stays aligned
                merged.origX = (oldA.origX ?? 0) + ((oldA.x ?? 0) - (oldA.origX ?? 0)) * (1 - k);
              }
              if (newA.height != null && oldA.height && newA.height !== oldA.height) {
                const k = newA.height / oldA.height;
                merged.origH = (oldA.origH ?? 0) * k;
                merged.origY = (oldA.origY ?? 0) + ((oldA.y ?? 0) - (oldA.origY ?? 0)) * (1 - k);
              }
              resolved = { ...resolved, attrs: merged };
            }
          }
          return {
            ...el,
            ...resolved,
            attrs: resolved.attrs ?? el.attrs,
            animation: resolved.animation !== undefined ? resolved.animation : el.animation,
            effects: resolved.effects !== undefined ? resolved.effects : el.effects,
          };
        })
      );
    },
    [commit]
  );

  // ---- Delete ----
  const deleteElements = useCallback(
    (ids) => {
      const set = new Set(ids);
      commit((prev) => prev.filter((el) => !set.has(el.id)));
      setSelectedIds([]);
    },
    [commit, setSelectedIds]
  );

  const deleteElement = useCallback((id) => deleteElements([id]), [deleteElements]);

  // ---- Duplicate (single + batch, preserves grouping) ----
  const duplicateElements = useCallback(
    (ids) => {
      const set = new Set(ids);
      const newIds = [];
      commit((prev) => {
        const groupMap = {};
        const copies = prev
          .filter((el) => set.has(el.id))
          .map((el) => {
            const copy = JSON.parse(JSON.stringify(el));
            copy.id = nanoIdLike();
            if (el.groupId) {
              if (!groupMap[el.groupId]) groupMap[el.groupId] = "grp_" + Math.random().toString(36).slice(2, 9);
              copy.groupId = groupMap[el.groupId];
            }
            copy.attrs = translateElement(copy, 24, 24);
            newIds.push(copy.id);
            return copy;
          });
        return [...prev, ...copies];
      });
      if (newIds.length) setSelectedIds(newIds);
    },
    [commit, setSelectedIds]
  );

  const duplicateElement = useCallback((id) => duplicateElements([id]), [duplicateElements]);

  // Duplicate the given element objects in place (no offset) and return the
  // freshly-created copies so the caller can immediately start dragging them.
  // Used by Alt/Opt + drag on the canvas: the original stays put and the copy
  // follows the pointer. Selection is updated to the copies.
  const duplicateForDrag = useCallback(
    (targetEls) => {
      if (!targetEls || !targetEls.length) return [];
      const groupMap = {};
      const copies = targetEls.map((el) => {
        const copy = JSON.parse(JSON.stringify(el));
        copy.id = nanoIdLike();
        if (el.groupId) {
          if (!groupMap[el.groupId]) groupMap[el.groupId] = "grp_" + Math.random().toString(36).slice(2, 9);
          copy.groupId = groupMap[el.groupId];
        }
        return copy;
      });
      commit((prev) => [...prev, ...copies]);
      setSelectedIds(copies.map((c) => c.id));
      return copies;
    },
    [commit, setSelectedIds]
  );

  // ---- Align ----
  const alignElements = useCallback(
    (ids, mode) => {
      const set = new Set(ids);
      commit((prev) => {
        const targets = prev.filter((el) => set.has(el.id));
        if (targets.length < 2) return prev;
        const u = unionBbox(targets);
        return prev.map((el) => {
          if (!set.has(el.id)) return el;
          const b = bboxOf(el);
          let dx = 0;
          let dy = 0;
          switch (mode) {
            case "left":
              dx = u.x - b.x;
              break;
            case "centerH":
              dx = u.x + u.w / 2 - (b.x + b.w / 2);
              break;
            case "right":
              dx = u.x + u.w - (b.x + b.w);
              break;
            case "top":
              dy = u.y - b.y;
              break;
            case "middleV":
              dy = u.y + u.h / 2 - (b.y + b.h / 2);
              break;
            case "bottom":
              dy = u.y + u.h - (b.y + b.h);
              break;
            default:
              break;
          }
          if (dx === 0 && dy === 0) return el;
          return { ...el, attrs: translateElement(el, dx, dy) };
        });
      });
    },
    [commit]
  );

  // ---- Layer order ----
  // Move the given ids to the end of the elements array (top of the SVG paint
  // order, i.e. rendered last = visually on top). No-op if already on top.
  const bringToFront = useCallback(
    (ids) => {
      if (!ids || !ids.length) return;
      const set = new Set(ids);
      commit((prev) => {
        const moving = prev.filter((el) => set.has(el.id));
        if (!moving.length) return prev;
        const rest = prev.filter((el) => !set.has(el.id));
        // Already at the top in the same relative order — nothing to do.
        const tail = prev.slice(prev.length - moving.length);
        const same =
          tail.length === moving.length && tail.every((el, i) => el.id === moving[i].id);
        if (same) return prev;
        return [...rest, ...moving];
      });
    },
    [commit]
  );

  // ---- Group / Ungroup ----
  const groupElements = useCallback(
    (ids) => {
      if (!ids || ids.length < 2) return;
      const set = new Set(ids);
      const gid = "grp_" + Math.random().toString(36).slice(2, 9);
      commit((prev) => prev.map((el) => (set.has(el.id) ? { ...el, groupId: gid } : el)));
      toast.success("Grouped " + ids.length + " objects");
    },
    [commit]
  );

  const ungroupElements = useCallback(
    (ids) => {
      const set = new Set(ids);
      commit((prev) =>
        prev.map((el) => {
          if (!set.has(el.id) || !el.groupId) return el;
          const { groupId, ...rest } = el;
          return rest;
        })
      );
      toast.success("Ungrouped");
    },
    [commit]
  );

  // ---- Image upload (drag/drop file, paste, or library button) ----
  // `point` is optional artboard coords; when omitted the image is placed at
  // a small offset from the origin so it's visible after a paste.
  // Transparent padding is auto-trimmed so the visible content fills the
  // element box — otherwise PNGs from design tools render much smaller than
  // their <image> tag and corner-radius clipping looks broken.
  const addImageFromFile = useCallback(
    async (file, point) => {
      try {
        const rawDataUrl = await readFileAsDataURL(file);
        const { dataUrl, width: natW, height: natH } = await trimTransparentPadding(rawDataUrl);
        const { width, height } = fitDimensions(natW, natH);
        const el = {
          id: nanoIdLike(),
          type: "image",
          attrs: {
            x: point ? point.x - width / 2 : 40,
            y: point ? point.y - height / 2 : 40,
            width,
            height,
            // Original-image box (equal to x/y/width/height until the image is
            // cropped). Kept in sync by translateElement/resizeElement so crop
            // mode can always restore the full source image.
            origX: point ? point.x - width / 2 : 40,
            origY: point ? point.y - height / 2 : 40,
            origW: width,
            origH: height,
            href: dataUrl,
            preserveAspectRatio: "none",
            opacity: 1,
          },
          animation: null,
          effects: {},
        };
        commit((prev) => [...prev, el]);
        setSelectedIds([el.id]);
        toast.success("图片已添加");
      } catch (err) {
        toast.error(err.message || "无法添加图片");
      }
    },
    [commit, setSelectedIds]
  );

  // ---- Drop from library ----
  const handleCanvasDrop = useCallback(
    (payload, point) => {
      // Native file drop (image dragged from OS into the canvas).
      if (payload.kind === "file" && payload.file) {
        addImageFromFile(payload.file, point);
        return;
      }
      if (payload.kind === "shape") {
        const def = BASIC_SHAPES.find((s) => s.id === payload.shapeId);
        if (!def) return;
        const el = def.create(point.x - 60, point.y - 45);
        commit((prev) => [...prev, el]);
        setSelectedIds([el.id]);
      } else if (payload.kind === "template") {
        const def = TEMPLATES.find((t) => t.id === payload.templateId);
        if (!def) return;
        const els = def.create(point.x - 60, point.y - 40);
        commit((prev) => [...prev, ...els]);
        setSelectedIds(els[0] ? [els[0].id] : []);
      } else if (payload.kind === "animation") {
        if (!selectedIds.length) {
          toast.error("Select an element first");
          return;
        }
        const preset = ANIMATION_PRESETS.find((p) => p.id === payload.presetId);
        if (preset) {
          const set = new Set(selectedIds);
          commit((prev) => prev.map((el) => (set.has(el.id) ? { ...el, animation: preset.build() } : el)));
        }
      }
    },
    [commit, selectedIds, setSelectedIds]
  );

  const applyAnimation = useCallback(
    (preset) => {
      if (!selectedIds.length) {
        toast.error("Select an element on the canvas first");
        return;
      }
      const set = new Set(selectedIds);
      commit((prev) => prev.map((el) => (set.has(el.id) ? { ...el, animation: preset.build() } : el)));
      toast.success(`Applied: ${preset.label}`);
    },
    [commit, selectedIds]
  );

  return {
    updateElement,
    deleteElement,
    deleteElements,
    duplicateElement,
    duplicateElements,
    duplicateForDrag,
    alignElements,
    groupElements,
    ungroupElements,
    bringToFront,
    handleCanvasDrop,
    applyAnimation,
    addImageFromFile,
  };
}
